import AdvancedSearchPhraseFilters from "./advanced-search-phrase-filters";
import classNames from "classnames";
import ResizeObserver from "resize-observer-polyfill";
import { Box, BoxContent, MenuOpenClosedIndicator } from "@edgetier/client-components";
import { doNothing } from "@edgetier/utilities";
import { FunctionComponent, memo, useRef, useState } from "react";

import { IFormValues } from "./advanced-search-phrase-filters/advanced-search-phrase-filters.types";
import { IInteractionsGetQueryParameters, TextSearchType } from "@edgetier/watchtower-types";
import { IProps } from "./advanced-search-phrase.types";
import { mergeRefs, useLayer } from "react-laag";
import { useFormikContext } from "formik";
import { isValueEmpty } from "utilities/is-value-empty";
import "./advanced-search-phrase.scss";

/**
 * Advanced Filter search phrase input
 * @param props.isDisabled      A boolean indicating that the field should be disabled.
 * @param props.name            Field name
 * @param props.placeholder     Field placeholder
 */
const AdvancedSearchPhrase: FunctionComponent<IProps> = ({ isDisabled = false, name, placeholder }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    // Get form values from Formik's context
    const { values, setValues } = useFormikContext<IInteractionsGetQueryParameters>();
    const { search, isAgentSearch, isBotSearch, isCustomerSearch, isMetadataSearch, isSystemSearch, textSearchType } =
        values;

    /**
     * Close the menu.
     */
    const close = () => setIsOpen(false);

    /**
     * Open or close the menu.
     */
    const toggle = () => {
        if (isDisabled) {
            return;
        }

        setIsOpen(!isOpen);

        if (!isOpen) {
            doNothing();
        }
    };

    const { renderLayer, triggerProps, layerProps } = useLayer({
        auto: true,
        isOpen,
        onOutsideClick: close,
        onParentClose: toggle,
        triggerOffset: 4,
        placement: "bottom-center",
        ResizeObserver,
    });

    /**
     * Apply phrase search value and any other options to the explore filters form
     * @param values    Phrase search form values
     */
    const applySearchFilters = (values: IFormValues) => {
        const { textSearchType, search, ...otherValues } = values;

        // Set new form values
        setValues((formValues) => ({
            ...formValues,
            search,
            ...otherValues,
            textSearchType,
        }));
    };

    return (
        <>
            {isOpen &&
                renderLayer(
                    <div
                        className={classNames("advanced-search-phrase__menu")}
                        {...layerProps}
                        style={{ ...layerProps.style }}
                        role="menu"
                        aria-label="advanced-search-phrase-menu"
                    >
                        <Box>
                            <BoxContent className="advanced-search-phrase">
                                <AdvancedSearchPhraseFilters
                                    initialValues={{
                                        [name]: search ?? "",
                                        textSearchType: isValueEmpty(search) ? TextSearchType.Simple : textSearchType,
                                        isAgentSearch: isValueEmpty(search) ? false : isAgentSearch,
                                        isBotSearch: isValueEmpty(search) ? false : isBotSearch,
                                        isCustomerSearch: isValueEmpty(search) ? true : isCustomerSearch,
                                        isMetadataSearch: isValueEmpty(search) ? false : isMetadataSearch,
                                        isSystemSearch: isValueEmpty(search) ? false : isSystemSearch,
                                    }}
                                    closeMenu={close}
                                    placeholder={placeholder}
                                    applySearchFilters={applySearchFilters}
                                    name={name}
                                />
                            </BoxContent>
                        </Box>
                    </div>
                )}
            <div
                aria-disabled={isDisabled}
                className={classNames("advanced-search-phrase__trigger", {
                    "advanced-search-phrase--is-disabled": isDisabled,
                })}
                {...triggerProps}
                ref={mergeRefs(triggerProps.ref, ref)}
                onClick={toggle}
            >
                {!isValueEmpty(search) ? (
                    <div className="advanced-search-phrase__placeholder">{search}</div>
                ) : (
                    <div className="advanced-search-phrase__placeholder advanced-search-phrase__placeholder--is-empty">
                        {placeholder}
                    </div>
                )}

                <MenuOpenClosedIndicator isOpen={isOpen} />
            </div>
        </>
    );
};

export default memo(AdvancedSearchPhrase);
